import * as React from "react";

import type { GlideFunctionalRenderer } from "common/components/uis";

import { loadedDefinedMap, asString, isLoadedDefined, asDate, asArrayValue } from "common/components/component-helpers";
import { definedMap } from "@glideapps/ts-necessities";
import type { CalendarEvent, Event } from "webapp/ui/views/calendar-schedule/calendar-schedule";
import EventPicker from "webapp/ui/smart/event-picker/event-picker";
import { logInfo } from "common/debug-print";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
export const EventPickerMaterialRenderer: GlideFunctionalRenderer = p => {
    const { getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[selectionStartPV, selectionEndPV, eventsPV], [selectionStartVP, selectionEndVP]] = useForceValueProducers(
        getValueProducers,
        force,
        ctx
    );

    const onSelectionChange = React.useCallback(
        (e: Event) => {
            logInfo("event picker selection changed", e);
            selectionStartVP?.set(e.start);
            selectionEndVP?.set(e.end);
        },
        [selectionEndVP, selectionStartVP]
    );

    const selectionStart = loadedDefinedMap(selectionStartPV, asDate);
    const selectionEnd = loadedDefinedMap(selectionEndPV, asDate);
    let selection: CalendarEvent<unknown> | undefined;
    if (selectionStart !== undefined && selectionEnd !== undefined) {
        selection = {
            start: selectionStart,
            end: selectionEnd,
            data: undefined,
            description: "",
            name: "",
            itemID: "",
        };
    }

    const events: CalendarEvent<unknown>[] = [];
    if (isLoadedDefined(eventsPV)) {
        for (const eventVP of asArrayValue(eventsPV)) {
            const eventInspector = eventVP.getObjectInspector(force);
            if (!isLoadedDefined(eventInspector)) continue;

            const start = loadedDefinedMap(definedMap(eventInspector.getProperty("start"), force), asDate);
            const end = loadedDefinedMap(definedMap(eventInspector.getProperty("end"), force), asDate);
            if (start === undefined) continue;

            const title = loadedDefinedMap(definedMap(eventInspector.getProperty("title"), force), asString);
            const desc = loadedDefinedMap(definedMap(eventInspector.getProperty("description"), force), asString);

            const startConverted = start;
            events.push({
                start: startConverted,
                end: end ?? startConverted,
                name: title ?? "",
                description: desc,
                data: undefined,
                itemID: "",
            });
        }
    }

    return <EventPicker events={events} selection={selection} onSelectionChange={onSelectionChange} />;
};
